import { type ReactNode, useMemo } from "react";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import type { CsticId, LinearCsticValue } from "~/features/configurator/Linear/models";
import { useCsticSelectProps } from "~/features/configurator/Shaft/components/useCsticSelectProps";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import type { DropdownEntry } from "~/common/types";

interface Props {
  csticId: CsticId;
  substepType: SubstepType;
  csticsToNull?: string[];
  csticsToSet?: Array<CsticToSet | Resolver>;
  disabled?: boolean;
  allowedValues?: string[];
  label?: string;
  helperLabel?: ReactNode;
  renderValue?: (value: LinearCsticValue, index: number) => ReactNode;
  shrink?: boolean;
  showFilter?: boolean;
  useCsticLabel?: boolean;
  required?: boolean;
}

type CsticToSet =
  | {
      key: string;
      value: string | null;
    }
  | undefined;

type Resolver = (value: string | null) => CsticToSet;

export const SimpleCsticSelectField = ({
  label,
  helperLabel,
  csticId,
  allowedValues,
  substepType,
  renderValue = DEFAULT_RENDER_VALUE,
  csticsToNull,
  csticsToSet,
  disabled: externalDisabled,
  useCsticLabel,
  required,
  ...rest
}: Props) => {
  const { selectProps, csticValues } = useCsticSelectProps({
    csticId,
    substep: substepType,
    allowedValues,
    csticsToNull,
    csticsToSet,
    disabled: externalDisabled,
    useCsticLabel,
    required,
  });

  const values: DropdownEntry<string>[] = useMemo(
    () =>
      csticValues.map((value, index) => {
        return { value: value.value, label: renderValue(value, index) };
      }),
    [csticValues, renderValue],
  );

  return (
    <SimpleSelect
      label={
        label ? <LanguageAwareExternalContent content={label} className={"scha__lc__external-label"} /> : undefined
      }
      helperText={helperLabel}
      fullWidth={true}
      values={values}
      {...rest}
      {...selectProps}
      onChange={(e) => selectProps.onChange(e.target.value as string)}
    />
  );
};

const DEFAULT_RENDER_VALUE = (value: LinearCsticValue) => value.label;
